import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Avatar,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { Print, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AuthService from "../Auth/AuthService";
import { printHtml } from "../utils/htmlToPrint";

const TimetableView = () => {
    const navigate = useNavigate();
    const [entryList, setEntryList] = useState([]);
    const [loading, setLoading] = useState(true);
    const hasFetched = useRef(false);
    const [times, setTimes] = useState();
    const [school, setSchool] = useState(null);

    useEffect(() => {
        try {
            const schoolData = sessionStorage.getItem("school");

            if (schoolData) {
                setSchool(JSON.parse(schoolData));
            }
        } catch (error) {
            console.error(
                "Error reading school from sessionStorage:",
                error
            );
        }
    }, []);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);

            const url = AuthService.pathUrl("GET", "/entry-card");
            const response = await AuthService.getAuth(url);

            const data = response || [];
            if (data.length > 0) {
                setTimes({
                    startTime: data[0].startTime || "",
                    endTime: data[0].endTime || "",
                });
            }
            if (Array.isArray(data)) {
                setEntryList(data);
            } else {
                console.error(
                    "Entry Card response is not an array:",
                    data
                );

                setEntryList([]);
            }
        } catch (error) {
            console.error(
                "Error fetching entry card:",
                error
            );

            setEntryList([]);

            alert(
                "Error while loading Entry Card."
            );
        } finally {
            setLoading(false);
        }
    };

    const getDayName = (dateString) => {
        if (!dateString) {
            return "-";
        }

        const [year, month, day] =
            dateString.split("-");

        const date = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
        );

        return date.toLocaleDateString("en-US", {
            weekday: "long",
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";

        const [year, month, day] =
            dateString.split("-");

        return `${day}-${month}-${year}`;
    };

    const handlePrint = () => {
        printHtml("entry-card");
        window.location.reload();
    };

    return (
        <Box
            sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                minHeight: "100vh",

                "@media print": {
                    p: 0,
                    backgroundColor: "#fff",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,

                    "@media print": {
                        display: "none",
                    },
                }}
            >
                <Button
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>

                <Button
                    startIcon={<Print />}
                    variant="contained"
                    onClick={handlePrint}
                    disabled={
                        loading ||
                        entryList.length === 0
                    }
                    sx={{
                        bgcolor: "#1b1914",
                        borderRadius: 5,
                    }}
                >
                    Print
                </Button>
            </Box>

            <Box
                id="entry-card"
                sx={{
                    maxWidth: 900,
                    mx: "auto",
                    backgroundColor: "#fff",
                    border: "2px solid #222",
                    p: 3,

                    "@media print": {
                        maxWidth: 700,
                        width: "100%",
                        border: "2px solid #222",
                        p: 2,
                        m: 0,
                    },
                }}
            >

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    {school?.logoUrl && (
                        <Avatar
                            src={school.logoUrl}
                            alt="School Logo"
                            sx={{
                                width: 75,
                                height: 75,
                                borderRadius: 1,
                            }}
                        />
                    )}

                    <Box
                        sx={{
                            flex: 1,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                textTransform:
                                    "uppercase",
                            }}
                        >
                            {school?.schoolName ||
                                "School Name"}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ mt: 0.5 }}
                        >
                            {[
                                school?.addressDTO
                                    ?.addressLine1,

                                school?.addressDTO
                                    ?.city,

                                school?.addressDTO
                                    ?.state,
                            ]
                                .filter(Boolean)
                                .join(", ")}
                        </Typography>

                        {school?.contactPhone && (
                            <Typography variant="body2">
                                Tel:{" "}
                                {school.contactPhone}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Divider
                    sx={{
                        borderColor: "#222",
                        mb: 2,
                    }}
                />

                <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{
                        fontWeight: 800,
                        textTransform:
                            "uppercase",
                        mb: 2,
                        textDecoration:
                            "underline",
                    }}
                >
                    Examination Time Table
                </Typography>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Examination Schedule
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        {[times?.startTime, times?.endTime].filter(Boolean).join(" - ")}
                    </Typography>
                </Box>

                {loading ? (
                    <Box
                        sx={{
                            py: 5,
                            textAlign: "center",
                        }}
                    >
                        <Typography>
                            Loading examination
                            schedule...
                        </Typography>
                    </Box>
                ) : entryList.length === 0 ? (

                    <Box
                        sx={{
                            py: 5,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="h6"
                            color="text.secondary"
                        >
                            No Entry Card data found.
                        </Typography>
                    </Box>
                ) : (

                    <TableContainer
                        component={Paper}
                        elevation={0}
                    >
                        <Table
                            size="small"
                            sx={{
                                border:
                                    "1px solid #777",

                                "& th, & td": {
                                    border:
                                        "1px solid #777",
                                    padding:
                                        "8px 10px",
                                },

                                "& th": {
                                    fontWeight: 700,
                                    backgroundColor:
                                        "#f5f5f5",
                                },

                                "@media print": {
                                    "& th": {
                                        backgroundColor:
                                            "#f5f5f5 !important",

                                        WebkitPrintColorAdjust:
                                            "exact",

                                        printColorAdjust:
                                            "exact",
                                    },
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        align="center"
                                    >
                                        <strong>
                                            Sr. No.
                                        </strong>
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                    >
                                        <strong>
                                            Subject
                                        </strong>
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                    >
                                        <strong>
                                            Day
                                        </strong>
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                    >
                                        <strong>
                                            Exam Date
                                        </strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {entryList.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <TableRow
                                            key={
                                                item.id ||
                                                index
                                            }
                                        >
                                            <TableCell align="center">
                                                {index +
                                                    1}
                                            </TableCell>

                                            <TableCell align="center">
                                                {
                                                    item.subject
                                                }
                                            </TableCell>

                                            <TableCell align="center">
                                                {getDayName(
                                                    item.examDate
                                                )}
                                            </TableCell>

                                            <TableCell align="center">
                                                {formatDate(
                                                    item.examDate
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box
                    sx={{
                        mt: 3,
                        p: 1.5,
                        border: "1px solid #999",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            mb: 0.5,
                        }}
                    >
                        Note:
                    </Typography>

                    <Typography variant="body2">
                        The admit card is essential
                        and must be carried to the
                        exam centre. Candidates may
                        also need to carry a valid
                        photo ID proof for written,
                        oral, and signal examinations.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent:
                            "space-between",
                        mt: 7,
                    }}
                >
                    <Box
                        sx={{
                            textAlign: "center",
                            minWidth: 150,
                        }}
                    >
                        <Box
                            sx={{
                                borderBottom:
                                    "1px solid #222",
                                mb: 1,
                            }}
                        />

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                            }}
                        >
                            Class Teacher
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            textAlign: "center",
                            minWidth: 150,
                        }}
                    >
                        <Box
                            sx={{
                                borderBottom:
                                    "1px solid #222",
                                mb: 1,
                            }}
                        />

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                            }}
                        >
                            Principal
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TimetableView;